import React, { useState, useEffect, useCallback } from 'react';
import { useTimeFilter } from '../contexts/TimeFilterContext';
import { usePortalAssignment } from '../queries/useAssignmentChunks';
import { useAssignmentRouteEnabled } from '../hooks/useAssignmentRouteEnabled';
import { 
  Activity, 
  Clock, 
  RefreshCw, 
  Calendar, 
  MapPin, 
  CheckCircle, 
  TriangleAlert, 
  Truck, 
  ChevronRight,
  ChevronUp,
  ChevronDown,
  LayoutGrid,
  ClipboardList,
  History,
  MessageSquare,
  Eye,
  MoreVertical,
  User
} from 'lucide-react';

// Helpers for raw API trip shape (original_plan uses API keys)
const tripId = (t) => t.tripId ?? t.trip_id ?? '—';
const clientName = (t) => t.priorityClient?.displayName ?? t.client_name ?? '—';
const scheduleTime = (t) => t.scheduleTime ?? t.schedule_time ?? '';
const appointmentTime = (t) => t.appointmentTime ?? t.appointment_time ?? '';
const pickupAddress = (t) => t.jobOriginAddress ?? t.pickup_address ?? '—';
const dropoffAddress = (t) => t.jobDestinationAddress ?? t.dropoff_address ?? '—';
const pickupLat = (t) => t.jobOriginLatitude ?? t.pickup_lat;
const pickupLng = (t) => t.jobOriginLongitude ?? t.pickup_lng;
const dropoffLat = (t) => t.jobDestinationLatitude ?? t.dropoff_lat;
const dropoffLng = (t) => t.jobDestinationLongitude ?? t.dropoff_lng;
const milageValue = (t) => t.milage ?? t.trip_distance_miles ?? 0;
const statusValue = (t) => t.jobStatus ?? t.status ?? '—';

const getSpaceTypeLabel = (t) => {
  if (t.losTitle && t.spaceTypeTitle) return `${t.losTitle} (${t.spaceTypeTitle})`;
  if (t.los_title && t.space_type_title) return `${t.los_title} (${t.space_type_title})`;
  return t.los_title || t.space_type || t.spaceTypeTitle || t.losTitle || '—';
};

const getFundingSource = (t) => t.funding_source_title || t.fundingSource || t.funding_source || '—';
const getServiceArea = (t) => t.service_area_title || t.serviceArea || t.service_area || '—';
const getAuthNumber = (t) => t.authorization_number || t.auth_number || '—';
const getPhone = (t) => t.phone_number || t.phone || '—';
const getNotes = (t) => t.dv_notes || t.notes || '—';
const getEscort = (t) => t.escort || '—';

const formatTime = (isoString, timeZone = 'America/Los_Angeles') => {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timeZone,
    });
  } catch {
    return String(isoString);
  }
};

const formatShortDate = (isoString, timeZone = 'America/Los_Angeles') => {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone: timeZone,
    });
  } catch {
    return '—';
  }
};

const formatShortTime = (isoString, timeZone = 'America/Los_Angeles') => {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timeZone,
    });
  } catch {
    return '—';
  }
};

const formatCoords = (lat, lng) => {
  if (lat == null || lng == null) return '—';
  return `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
};

// One row per column: shared by header, colgroup, and TripRow order.
const PORTAL_PLAN_COLUMNS = [
  { label: '#', width: '50px' },
  { label: 'Trip ID', width: '150px' },
  { label: 'Client', width: '180px' },
  { label: 'Schedule', width: '130px' },
  { label: 'Appt', width: '130px' },
  { label: 'Pickup', width: '250px' },
  { label: 'Dropoff', width: '250px' },
  { label: 'Mi', width: '70px', align: 'right' },
  { label: 'Status', width: '100px' },
  { label: 'Pickup coords', width: '130px' },
  { label: 'Dropoff coords', width: '130px' },
];

const PortalPlanColgroup = () => (
  <colgroup>
    {PORTAL_PLAN_COLUMNS.map((col, i) => (
      <col key={i} style={{ width: col.width }} />
    ))}
  </colgroup>
);

const TripRow = ({ trip, index, timezone }) => (
  <tr className="group text-main-dark even:bg-main-base-100 hover:bg-slate-50 border-b border-gray-300">
    {/* # */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full text-xs text-center font-black">{index + 1}</div>
    </td>
    {/* Trip ID */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full text-xs font-medium text-main-primary whitespace-nowrap overflow-hidden">{tripId(trip)}</div>
    </td>
    {/* Client */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full overflow-hidden flex items-center gap-1 flex-wrap">
        <span className="text-xs font-medium truncate" title={clientName(trip)}>{clientName(trip)}</span>
        {trip.preferred_driver_info?.preferred_driver_name && (
          <span className="text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 py-0.5 rounded leading-none whitespace-nowrap">
            Pref: {trip.preferred_driver_info.preferred_driver_name}
          </span>
        )}
      </div>
    </td>
    {/* Schedule */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full text-xs whitespace-nowrap overflow-hidden">{formatTime(scheduleTime(trip), timezone)}</div>
    </td>
    {/* Appt */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full text-xs whitespace-nowrap overflow-hidden">{formatTime(appointmentTime(trip), timezone)}</div>
    </td>
    {/* Pickup address */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full text-xs truncate" title={pickupAddress(trip)}>{pickupAddress(trip)}</div>
    </td>
    {/* Dropoff address */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full text-xs truncate" title={dropoffAddress(trip)}>{dropoffAddress(trip)}</div>
    </td>
    {/* Mi */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full text-xs font-bold whitespace-nowrap text-right">{milageValue(trip) != null ? Number(milageValue(trip)).toFixed(1) : '—'}</div>
    </td>
    {/* Status */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full flex items-center">
        <span className="inline-flex items-center rounded bg-main-primary px-1.5 py-0.5 text-[9px] font-medium text-white whitespace-nowrap">
          {statusValue(trip)}
        </span>
      </div>
    </td>
    {/* Pickup coords */}
    <td className="p-[3px] border-r border-main-base/10">
      <div className="w-full text-xs text-main-light whitespace-nowrap overflow-hidden">{formatCoords(pickupLat(trip), pickupLng(trip))}</div>
    </td>
    {/* Dropoff coords */}
    <td className="p-[3px] border-r border-gray-200">
      <div className="w-full text-xs text-main-light whitespace-nowrap overflow-hidden">{formatCoords(dropoffLat(trip), dropoffLng(trip))}</div>
    </td>
  </tr>
);

const PortalPlanTripsTable = ({ trips, timezone, driverInfo, isUnassigned = false }) => {
  const [isOpen, setIsOpen] = useState(true);

  const headerBg = isUnassigned ? 'bg-rose-50' : 'bg-white';
  const titleColor = isUnassigned ? 'text-rose-700' : 'text-textPrimary';

  return (
    <div className={`shadow ring-1 ${isUnassigned ? 'ring-rose-200' : 'ring-main-base'} rounded-lg mb-6 bg-white overflow-x-auto lg:overflow-visible`}>
      <table className="w-full min-w-[900px] table-fixed border-separate border-spacing-0 text-left">
        <PortalPlanColgroup />
        <thead className="z-30">
          {driverInfo && (
            <tr className={headerBg}>
              <th
                colSpan={PORTAL_PLAN_COLUMNS.length}
                onClick={() => setIsOpen(!isOpen)}
                className={`sticky top-0 z-40 px-3 h-10 border-b ${isUnassigned ? 'border-rose-200' : 'border-main-base'} ${headerBg} cursor-pointer select-none ${isUnassigned ? 'hover:bg-rose-100/60' : 'hover:bg-slate-50'} transition-colors`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isUnassigned ? (
                      <div className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center">
                        <TriangleAlert className="w-3.5 h-3.5 text-rose-600" />
                      </div>
                    ) : (
                      driverInfo.img ? (
                        <img src={driverInfo.img} alt="" className="w-6 h-6 rounded-full object-cover border border-card-border" />
                      ) : (
                        <span className="w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center text-slate-600 text-[10px] font-bold">
                          {driverInfo.name.charAt(0)}
                        </span>
                      )
                    )}
                    <span className={`text-sm font-bold ${titleColor}`}>{driverInfo.name}</span>
                    <span className={`${isUnassigned ? 'text-rose-400' : 'text-main-light'} font-normal text-xs`}>({trips.length} trips)</span>
                  </div>
                  <div className={`p-1 ${isUnassigned ? 'text-rose-500' : 'text-main-light'}`}>
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
              </th>
            </tr>
          )}
          {isOpen && (
            <tr>
              {PORTAL_PLAN_COLUMNS.map((col, idx) => (
                <th
                  key={idx}
                  className={`sticky ${driverInfo ? 'top-[40px]' : 'top-0'} z-30 border-b border-gray-300 bg-white p-[3px] text-left text-sm font-semibold text-gray-900`}
                >
                  <div className={`w-full whitespace-nowrap overflow-hidden ${col.label === '#' ? 'text-center' : col.align === 'right' ? 'text-right' : ''}`}>
                    {col.label}
                  </div>
                </th>
              ))}
            </tr>
          )}
        </thead>
        {isOpen && (
          <tbody className="bg-white">
            {trips.map((t, i) => (
              <TripRow key={i} trip={t} index={i} timezone={timezone} />
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

const PortalPlanPage = () => {
  const { selectedResultId, setOnPublishSuccess, patchAssignmentSessionCache } = useTimeFilter();
  const portalRouteEnabled = useAssignmentRouteEnabled('portal');
  const {
    normalized: sessionNormalized,
    isLoading,
    isFetching,
    error: queryError,
  } = usePortalAssignment(selectedResultId, { enabled: portalRouteEnabled });

  const sessionLoading =
    Boolean(selectedResultId) &&
    !selectedResultId.startsWith('mock-') &&
    (isLoading || isFetching) &&
    !sessionNormalized;

  const originalPlan = sessionNormalized?.original_plan ?? null;
  const metadata = sessionNormalized?.metadata ?? null;

  const loadError = (() => {
    if (!selectedResultId || selectedResultId.startsWith('mock-')) return null;
    if (sessionLoading) return null;
    if (queryError) return 'Failed to load Portal Plan for this session.';
    if (!sessionNormalized) return 'This session has no Portal Plan data (run a new planner to capture it).';
    if (!originalPlan) return 'This session has no Portal Plan data (run a new planner to capture it).';
    return null;
  })();

  const drivers = originalPlan?.drivers ?? [];
  const unassignedTrips = originalPlan?.unassigned_trips ?? [];
  const assignedCount = drivers.reduce((sum, b) => sum + (b.trips?.length ?? 0), 0);
  const totalCount = assignedCount + unassignedTrips.length;
  const companyName = metadata?.company_name ?? '';
  const planningDate = metadata?.planning_date ?? '';
  const companyTimezone = metadata?.timezone || 'America/Los_Angeles';

  const handlePublishSuccess = useCallback((type) => {
    if (!selectedResultId) return;
    patchAssignmentSessionCache(selectedResultId, {
      publishFlags: type === 'portal'
        ? { portalPlanPublished: true }
        : { isPublished: true },
    });
  }, [selectedResultId, patchAssignmentSessionCache]);

  useEffect(() => {
    setOnPublishSuccess(() => handlePublishSuccess);
    return () => setOnPublishSuccess(null);
  }, [handlePublishSuccess, setOnPublishSuccess]);

  return (
    <div className="space-y-3 w-full px-1">
      {/* Modernized Scheduler Header & Stats Bar */}
      {originalPlan && (
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden mb-4">
          <div className="px-2 py-2">
            <div className="grid gap-2 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
              {/* Company Card */}
              <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                <dl className="flex items-center h-full">
                  <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                    <Activity className="text-dashboard-stats-primary h-3.5 w-3.5" />
                  </dt>
                  <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                    <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1 uppercase">Company</div>
                    <div className="text-dashboard-stats-primary truncate text-xs font-bold uppercase">{companyName || 'NEMT Prime'}</div>
                  </dd>
                </dl>
              </div>

              {/* Date Card */}
              <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                <dl className="flex items-center h-full">
                  <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                    <Calendar className="text-dashboard-stats-primary h-3.5 w-3.5" />
                  </dt>
                  <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                    <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1 uppercase">Date</div>
                    <div className="text-dashboard-stats-primary truncate text-[11px] font-bold tabular-nums whitespace-nowrap">{formatTime(planningDate) || 'Apr 16, 2026'}</div>
                  </dd>
                </dl>
              </div>

              {/* Total Trips Card */}
              <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                <dl className="flex items-center h-full">
                  <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                    <MapPin className="text-dashboard-stats-primary h-3.5 w-3.5" />
                  </dt>
                  <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                    <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1 uppercase">Total Trips</div>
                    <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums">{totalCount}</div>
                  </dd>
                </dl>
              </div>

              {/* Assigned Card */}
              <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                <dl className="flex items-center h-full">
                  <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                    <CheckCircle className="text-dashboard-stats-primary h-3.5 w-3.5" />
                  </dt>
                  <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                    <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1 uppercase">Assigned</div>
                    <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums text-emerald-600">{assignedCount}</div>
                  </dd>
                </dl>
              </div>

              {/* Unassigned Card */}
              <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                <dl className="flex items-center h-full">
                  <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                    <TriangleAlert className="text-dashboard-stats-primary h-3.5 w-3.5" />
                  </dt>
                  <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                    <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1 uppercase">Unassigned</div>
                    <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums text-rose-600">{unassignedTrips.length}</div>
                  </dd>
                </dl>
              </div>

              {/* Drivers Card */}
              <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                <dl className="flex items-center h-full">
                  <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                    <Truck className="text-dashboard-stats-primary h-3.5 w-3.5" />
                  </dt>
                  <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                    <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1 uppercase">Drivers</div>
                    <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums">{drivers.length}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}

      {loadError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded text-sm">
          {loadError}
        </div>
      )}

      {sessionLoading && (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {!sessionLoading && originalPlan && (
        <div className="pb-4 space-y-6">
          {/* One table per driver */}
          {drivers.map((bucket, idx) => {
            const driver = bucket.driver || {};
            const trips = bucket.trips || [];
            const name = driver.displayName ?? driver.driver_name ?? 'Unknown';
            const img = driver.profileImageURL ?? '';
            return (
              <PortalPlanTripsTable
                key={idx}
                trips={trips}
                timezone={companyTimezone}
                driverInfo={{ name, img }}
              />
            );
          })}

          {/* Unassigned trips — single table at bottom */}
          {unassignedTrips.length > 0 && (
            <div className="mt-8">
              <div className="relative z-[41] flex items-center gap-3 mb-4 px-1">
                <div className="h-px flex-1 bg-rose-100"></div>
                <div className="flex items-center gap-2 py-1 px-3 bg-rose-50 rounded-full border border-rose-100">
                  <TriangleAlert className="w-4 h-4 text-rose-500" />
                  <h3 className="text-xs font-bold text-rose-700 uppercase tracking-wider">Unassigned</h3>
                  <span className="bg-rose-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                    {unassignedTrips.length}
                  </span>
                </div>
                <div className="h-px flex-1 bg-rose-100"></div>
              </div>
              
              <PortalPlanTripsTable
                trips={unassignedTrips}
                timezone={companyTimezone}
                driverInfo={{ name: 'Unassigned', img: '' }}
                isUnassigned={true}
              />
            </div>
          )}

          {unassignedTrips.length === 0 && (
            <div className="shadow ring-1 ring-main-base rounded-lg px-4 py-8 text-center text-sm text-textMuted bg-white">
              No unassigned trips found for this session.
            </div>
          )}
        </div>
      )}

      {!sessionLoading && !originalPlan && !loadError && !selectedResultId && (
        <div className="text-center py-8 text-sm text-textMuted">
          Select a planning session above to view its Portal Plan.
        </div>
      )}
    </div>
  );
};

export default PortalPlanPage;
